import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import './Header.css';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  // Check if we're on the home page for anchor links
  const isHomePage = location.pathname === '/';

  return (
    <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
      <div className="header__container">
        <Link to="/" className="header__logo">
          <img src="/app-icon.png" alt="BalanzFit" className="header__logo-icon" />
          <span className="header__logo-text">BalanzFit</span>
        </Link>

        <nav className={`header__nav ${isMenuOpen ? 'header__nav--open' : ''}`}>
          <ul className="header__menu">
            <li>
              <a href={isHomePage ? '#features' : '/#features'} className="header__menu-item" onClick={handleNavClick}>
                {t('header.features')}
              </a>
            </li>
            <li>
              <a href={isHomePage ? '#how-it-works' : '/#how-it-works'} className="header__menu-item" onClick={handleNavClick}>
                {t('header.howItWorks')}
              </a>
            </li>
            <li>
              <a href={isHomePage ? '#about' : '/#about'} className="header__menu-item" onClick={handleNavClick}>
                {t('header.about')}
              </a>
            </li>
            <li>
              <a href={isHomePage ? '#pricing' : '/#pricing'} className="header__menu-item" onClick={handleNavClick}>
                {t('header.pricing')}
              </a>
            </li>
          </ul>
          <div className="header__actions">
            <div className="header__language-switcher">
              <button 
                className={`header__lang-btn ${i18n.language === 'nl' ? 'header__lang-btn--active' : ''}`}
                onClick={() => changeLanguage('nl')}
                aria-label="Nederlands"
              >
                NL
              </button>
              <button 
                className={`header__lang-btn ${i18n.language === 'en' ? 'header__lang-btn--active' : ''}`}
                onClick={() => changeLanguage('en')}
                aria-label="English"
              >
                EN
              </button>
            </div>
            <ThemeToggle />
            <a href={isHomePage ? '#download' : '/#download'} className="header__cta" onClick={handleNavClick}>
              {t('header.startFree')}
            </a>
          </div>
        </nav>

        <button 
          className={`header__hamburger ${isMenuOpen ? 'header__hamburger--active' : ''}`}
          onClick={toggleMenu}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
};

export default Header;
